import React, { useState, useEffect } from 'react'
import { Form, FormGroup, Label, Input, Modal, ModalHeader, ModalBody, Button, Badge } from 'reactstrap'
import DataTable from 'react-data-table-component'
import { getDataWithStudent, deleteIdData, getIdData, addData as addActivityData, updateData as updateActivityData } from '../../lib/activitylist'
import { getNotesData, addData, updateData } from '../../lib/pagenotes'
import { useAuth } from '../../_helper/Auth/AuthProvider'
import parse from 'html-react-parser'
import { getStudentActivityColumns } from '../../Data/ActivitiesAwards/StudentActivityListData'
import {
	ClassForLessonSelectForActivities,
	ActivityTierSelect,
	ActivityRowStatusSelect,
	CategorySelectForActivity,
	PositionSelectForActivity
} from '../Common/FormSelects'
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents'
import { ConfirmModal } from '../Common/ConfirmModal'

const ActivityExpandedDescriptionEditor = ({ row, readOnly, isDarkMode, onUpdated, onShowToast }) => {
	const [text, setText] = useState(row.description || '')
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		setText(row.description || '')
	}, [row.id, row.description])

	const panelStyle = {
		backgroundColor: isDarkMode ? '#1d1e26' : '#f8f9fa',
		color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'inherit',
		borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : '#dee2e6'
	}

	const saveDescription = async () => {
		const next = text ?? ''
		const prev = row.description || ''
		if (next === prev) return
		setSaving(true)
		try {
			await updateActivityData({ id: row.id, description: next })
			await onUpdated()
			onShowToast('Description updated successfully!')
		} catch (error) {
			console.error('Error updating description:', error)
			onShowToast('Could not save description.')
		} finally {
			setSaving(false)
		}
	}

	if (readOnly) {
		return (
			<div className="p-3 rounded border mx-2 mb-2" style={panelStyle}>
				<strong style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Description</strong>
				<div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>
					{(row.description || '').trim() ? row.description : '—'}
				</div>
			</div>
		)
	}

	return (
		<div className="p-3 rounded border mx-2 mb-2" style={panelStyle}>
			<strong style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Description</strong>
			<div className="mt-2 position-relative">
				<Input
					type="textarea"
					rows={4}
					value={text}
					onChange={(e) => setText(e.target.value)}
					className="form-control"
					style={{
						resize: 'vertical',
						minHeight: '100px',
						paddingBottom: '2.5rem',
						backgroundColor: isDarkMode ? '#2c323f' : '#fff',
						color: isDarkMode ? '#fff' : '#000',
						borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : '#ced4da'
					}}
				/>
				<Badge
					color="secondary"
					pill
					className="position-absolute"
					style={{ bottom: '10px', right: '12px', fontSize: '0.75rem' }}
				>
					{(text || '').length}
				</Badge>
			</div>
			<div className="mt-2 d-flex justify-content-end">
				<Button color="primary" size="sm" disabled={saving || (text ?? '') === (row.description || '')} onClick={saveDescription}>
					{saving ? 'Saving...' : 'Update'}
				</Button>
			</div>
		</div>
	)
}

const StudentActivitiesList = ({ studentId, isStudentView = true, isProfile = false }) => {
	const [activitylist, setActivityList] = useState([])
	const [description, setDescription] = useState('')
	const [recId, setRecId] = useState()
	const [pageNotesStatus, setPageNotesStatus] = useState('')
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)
	const [loading, setLoading] = useState(true)
	const [isDarkMode, setIsDarkMode] = useState(false)
	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const [deleteId, setDeleteId] = useState(null)
	const [expandedActivityRows, setExpandedActivityRows] = useState({})

	// Modal states
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [editMode, setEditMode] = useState('Add')
	const [editId, setEditId] = useState(null)
	const [formLoading, setFormLoading] = useState(false)
	
	// Form states
	const [gradelevel, setGradelevel] = useState()
	const [hoursperweek, setHoursperweek] = useState()
	const [weeksperyear, setWeeksperyear] = useState()
	const [recoglevel, setRecoglevel] = useState()
	const [sortOrder, setSortOrder] = useState(0)
	const [category, setCategory] = useState()
	const [position, setPosition] = useState()
	const [activityDescription, setActivityDescription] = useState()
	const [activityTitle, setActivityTitle] = useState('')
	const [activityRowStatus, setActivityRowStatus] = useState('')

	const { user } = useAuth()

	const pageName = 'StudentActivityGoals'

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	const handleDelete = (id) => {
		setDeleteId(id)
		setShowConfirmModal(true)
	}

	const confirmDelete = async () => {
		if (deleteId) {
			try {
				await deleteIdData(deleteId)
				await getDatafromActivityList()
				showToastMessage('Activity deleted successfully!')
			} catch (error) {
				console.error('Error deleting activity:', error)
			} finally {
				setDeleteId(null)
			}
		}
	}

	const toggleModal = () => {
		setIsModalOpen(!isModalOpen)
		if (isModalOpen) {
			resetForm()
		}
	}

	const resetForm = () => {
		setEditMode('Add')
		setEditId(null)
		setActivityTitle('')
		setActivityRowStatus('New')
		setGradelevel('')
		setHoursperweek('')
		setWeeksperyear('')
		setRecoglevel('')
		setSortOrder(0)
		setCategory('')
		setPosition('')
		setActivityDescription('')
	}

	const openAddModal = () => {
		resetForm()
		setIsModalOpen(true)
	}

	const openEditModal = async (id) => {
		try {
			setFormLoading(true)
			setIsModalOpen(true)
			const activityData = await getIdData(id)
			if (activityData && activityData.length > 0) {
				const row = activityData[0]
				setEditMode('Update')
				setEditId(id)
				setActivityTitle(row.title || '')
				setActivityRowStatus(row.status || 'New')
				setActivityDescription(row.description || '')
				setCategory(row.category || '')
				setPosition(row.position || '')
				setGradelevel(row.gradelevel || '')
				setRecoglevel(row.recoglevel || '')
				setHoursperweek(row.hoursperweek || '')
				setWeeksperyear(row.weeksperyear || '')
				setSortOrder(row.sortorder || 0)
			}
		} catch (error) {
			console.error('Error fetching activity:', error)
		} finally {
			setFormLoading(false)
		}
	}

	const handleActivitySubmit = async (e) => {
		e.preventDefault()
		setFormLoading(true)

		try {
			const formData = {
				studentId: isStudentView ? user?.id : studentId,
				title: (activityTitle || '').slice(0, 50),
				status: activityRowStatus || 'New',
				gradelevel,
				recoglevel,
				hoursperweek: hoursperweek === '' ? 0 : hoursperweek,
				weeksperyear: weeksperyear === '' ? 0 : weeksperyear,
				description: activityDescription,
				category,
				position,
				sortorder: sortOrder
			}

			if (editMode === 'Add') {
				await addActivityData(formData)
			} else {
				await updateActivityData({ id: editId, ...formData })
			}

			await getDatafromActivityList()
			
			showToastMessage(editMode === 'Add' ? 'Activity added successfully!' : 'Activity updated successfully!')
			toggleModal()
		} catch (error) {
			console.error('Error saving activity:', error)
		} finally {
			setFormLoading(false)
		}
	}

	const getDatafromActivityList = async (options = {}) => {
		const { silent } = options
		try {
			if (!silent) setLoading(true)
			const activity = await getDataWithStudent(studentId)
			setActivityList(activity)
		} catch (error) {
			console.error('Error fetching activities:', error)
		} finally {
			if (!silent) setLoading(false)
		}
	}

	const recordCheck = async () => {
		try {
			const sdata = await getNotesData(studentId, '', pageName)
			if (sdata.length === 0) {
				setPageNotesStatus('Add')
			} else {
				setPageNotesStatus('Update')
				setRecId(sdata[0].id)
				getNotes()
			}
		} catch (error) {
			console.error('Error checking records:', error)
		}
	}

	const getNotes = async () => {
		try {
			const sdata = await getNotesData(studentId, '', pageName)
			if (sdata && sdata.length > 0) {
				setDescription(sdata[0].instructorNote || '')
				setRecId(sdata[0].id)
			}
		} catch (error) {
			console.error('Error fetching notes:', error)
		}
	}

	const handleSubmitforGoals = async (e) => {
		e.preventDefault()
		try {
			if (pageNotesStatus === 'Add') {
				await addData({
					page: pageName,
					studentId: studentId,
					instructorNote: description
				})
				setPageNotesStatus('Update')
			} else {
				await updateData({
					id: recId,
					page: pageName,
					studentId: studentId,
					instructorNote: description
				})
			}
			showToastMessage('Goals and Recommendation updated successfully!')
		} catch (error) {
			console.error('Error saving goals:', error)
		}
	}

	useEffect(() => {
		if (studentId) {
			recordCheck()
			getDatafromActivityList()
		}
	}, [studentId])

	useEffect(() => {
		const checkDarkMode = () => {
			setIsDarkMode(document.body.classList.contains('dark-only'))
		}
		checkDarkMode()
		const observer = new MutationObserver(checkDarkMode)
		observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
		return () => observer.disconnect()
	}, [])


	const columns = getStudentActivityColumns(handleDelete, isProfile, openEditModal)

	const ActivityDescriptionExpandedRow = ({ data }) => (
		<ActivityExpandedDescriptionEditor
			row={data}
			readOnly={isProfile}
			isDarkMode={isDarkMode}
			onUpdated={() => getDatafromActivityList({ silent: true })}
			onShowToast={showToastMessage}
		/>
	)

	return (
		<div className="col-12 p-0">
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
			<div className="d-flex p-2 pb-0 justify-content-between align-items-center">
				<h4 className="px-2">{!isProfile && 'Student Activity'}</h4>
				{!isProfile && (
					<button
						onClick={openAddModal}
						className="btn btn-primary btn-sm"
					>
						<i className="fa fa-plus me-2"></i>
						Add New Activity
					</button>
				)}
			</div>

			{loading ? (
				<div className="text-center p-4">
					<div className="spinner-border" role="status">
						<span className="visually-hidden">Loading...</span>
					</div>
					<p className="mt-2">Loading activities...</p>
				</div>
			) : (
				<>
					<div className="px-2">
						<div className="table-responsive rdt-head-wrap rdt-activity-table">
							<DataTable
								columns={columns}
								data={activitylist}
								striped
								pagination
								paginationPerPage={10}
								paginationRowsPerPageOptions={[10, 25, 50, 100]}
								highlightOnHover
								responsive
								noDataComponent="No activities found"
								expandableRows
								expandableRowsComponent={ActivityDescriptionExpandedRow}
								expandableRowExpanded={(row) => !!expandedActivityRows[row.id]}
								onRowExpandToggled={(expanded, row) => {
									setExpandedActivityRows((prev) => ({
										...prev,
										[row.id]: expanded
									}))
								}}
								expandableIcon={{
									expanded: <i className="fa fa-chevron-down" />,
									collapsed: <i className="fa fa-chevron-right" />
								}}
								customStyles={{
									headRow: {
										style: {
											alignItems: 'stretch',
											minHeight: 'unset',
											backgroundColor: isDarkMode ? '#2c323f' : '#fff'
										}
									},
									headCells: {
										style: {
											fontWeight: 'bold',
											fontSize: '11px',
											whiteSpace: 'normal',
											overflow: 'visible',
											textOverflow: 'clip',
											wordBreak: 'break-word',
											lineHeight: 1.25,
											minHeight: '48px',
											padding: '12px 8px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'flex-start',
											textAlign: 'left',
											color: isDarkMode ? '#fff' : '#000',
											backgroundColor: isDarkMode ? '#2c323f' : '#fff'
										}
									},
									rows: {
										style: {
											color: isDarkMode ? '#fff' : '#000',
											backgroundColor: isDarkMode ? '#2c323f' : '#fff'
										}
									},
									cells: {
										style: {
											fontSize: '12px',
											padding: '8px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'flex-start',
											textAlign: 'left',
											color: isDarkMode ? '#fff' : '#000'
										}
									},
									pagination: {
										style: {
											color: isDarkMode ? '#fff' : '#000',
											backgroundColor: isDarkMode ? '#2c323f' : '#fff'
										}
									}
								}}
							/>
						</div>
					</div>

					<div className="px-2 mt-4">

						{/* Goals and Recommendation - Students can only view */}
						{isStudentView ? (
							<div className="p-3 rounded border">
								<h6 className="fw-bold mb-2" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Goals and Recommendation:</h6>
								<div style={{ color: isDarkMode ? '#fff' : 'inherit' }}>{description ? parse(description) : 'No goals and recommendations available'}</div>
							</div>
						) : (
							<Form onSubmit={handleSubmitforGoals}>
								<FormGroup>
									<Label className="form-control-label fw-bold mb-2" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Goals and Recommendation</Label>
									<Input
										type="textarea"
										rows="4"
										value={description}
										onChange={(e) => setDescription(e.target.value)}
										className="form-control border-radius-10"
										placeholder="Enter goals and recommendations"
										style={{
											resize: 'vertical',
											minHeight: '100px',
											border: '1px solid #e9ecef',
											boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
										}}
									/>
								</FormGroup>

								<div className="d-flex justify-content-end mt-3">
									<button type="submit" className="btn btn-primary">
										<i className="fa fa-save me-2"></i>
										Update Goals and Recommendation
									</button>
								</div>
							</Form>
						)}
					</div>
				</>
			)}

			{/* Activity Modal */}
			<Modal isOpen={isModalOpen} toggle={toggleModal} size="lg" centered>
				<ModalHeader toggle={toggleModal}>
					{editMode === 'Add' ? 'Add Activity' : 'Update Activity'}
				</ModalHeader>
				<ModalBody>
					{formLoading && editMode === 'Update' ? (
						<div className="text-center p-4">
							<div className="spinner-border" role="status">
								<span className="visually-hidden">Loading...</span>
							</div>
						</div>
					) : (
						<Form onSubmit={handleActivitySubmit}>
							<FormGroup className="mb-3">
								<Label className="form-control-label fw-bold mb-2">Title</Label>
								<Input
									type="text"
									maxLength={50}
									value={activityTitle}
									onChange={(e) => setActivityTitle(e.target.value)}
									className="form-control border-radius-10"
									placeholder="Title (max 50 characters)"
									style={{
										border: '1px solid #e9ecef',
										boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
										height: '45px',
										padding: '10px 15px'
									}}
								/>
								<small className="text-muted">{activityTitle.length}/50</small>
							</FormGroup>

							<FormGroup className="mb-3">
								<ActivityRowStatusSelect value={activityRowStatus} setValue={setActivityRowStatus} />
							</FormGroup>

							<FormGroup className="mb-3">
								<ClassForLessonSelectForActivities value={gradelevel} setValue={setGradelevel} />
							</FormGroup>

							<FormGroup className="mb-3">
								<Label className="form-control-label fw-bold mb-2">Hours Per Week</Label>
								<Input
									type="number"
									value={hoursperweek}
									onChange={(e) => setHoursperweek(e.target.value)}
									className="form-control border-radius-10"
									placeholder="Hours Per Week"
									min="0"
									style={{
										border: '1px solid #e9ecef',
										boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
										height: '45px',
										padding: '10px 15px'
									}}
								/>
							</FormGroup>

							<FormGroup className="mb-3">
								<Label className="form-control-label fw-bold mb-2">Weeks Per Year</Label>
								<Input
									type="number"
									value={weeksperyear}
									onChange={(e) => setWeeksperyear(e.target.value)}
									className="form-control border-radius-10"
									placeholder="Weeks Per Year"
									min="0"
									style={{
										border: '1px solid #e9ecef',
										boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
										height: '45px',
										padding: '10px 15px'
									}}
								/>
							</FormGroup>

							<FormGroup className="mb-3">
								<ActivityTierSelect value={recoglevel} setValue={setRecoglevel} />
							</FormGroup>

							<FormGroup className="mb-3">
								<Label className="form-control-label fw-bold mb-2">Category</Label>
								<CategorySelectForActivity value={category} setValue={setCategory} hideLabel />
							</FormGroup>

							<FormGroup className="mb-3">
								<Label className="form-control-label fw-bold mb-2">Position</Label>
								<PositionSelectForActivity value={position} setValue={setPosition} hideLabel />
							</FormGroup>

							<FormGroup className="mb-3">
								<Label className="form-control-label fw-bold mb-2">Description</Label>
								<div className="position-relative">
									<Input
										type="textarea"
										rows={4}
										value={activityDescription}
										onChange={(e) => setActivityDescription(e.target.value)}
										className="form-control border-radius-10"
										placeholder="Description"
										style={{
											resize: 'vertical',
											minHeight: '100px',
											paddingBottom: '2.5rem',
											border: '1px solid #e9ecef',
											boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
										}}
									/>
									<Badge color="secondary" pill className="position-absolute" style={{ bottom: '10px', right: '12px', fontSize: '0.75rem' }}>
										{(activityDescription || '').length}
									</Badge>
								</div>
							</FormGroup>

							<FormGroup className="mb-3">
								<Label className="form-control-label fw-bold mb-2">Order</Label>
								<Input
									type="number"
									value={sortOrder}
									onChange={(e) => setSortOrder(e.target.value)}
									className="form-control border-radius-10"
									required
									style={{
										border: '1px solid #e9ecef',
										boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
										height: '45px',
										padding: '10px 15px'
									}}
								/>
							</FormGroup>

							<div className="d-flex justify-content-between mt-4">
								<button type="submit" className="btn btn-primary" disabled={formLoading}>
									<i className="fa fa-save me-2"></i>
									{formLoading ? 'Saving...' : editMode === 'Add' ? 'Add Activity' : 'Update Activity'}
								</button>
								<button type="button" onClick={toggleModal} className="btn btn-secondary">
									Cancel
								</button>
							</div>
						</Form>
					)}
				</ModalBody>
			</Modal>
			<ConfirmModal
				isOpen={showConfirmModal}
				toggle={() => setShowConfirmModal(false)}
				onConfirm={confirmDelete}
				title="Delete Activity"
				message="Are you sure you want to delete this activity? This action cannot be undone."
			/>
		</div>
	)
}

export { StudentActivitiesList }
